import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { AlertCircle } from 'lucide-react';
import { getUserName } from 'helpers';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Spinner } from 'components/ui/spinner';
import { cn } from 'lib/utils';
import { findAgencyByName } from '../agencyTransferUtils';

const normalRules = [
  'Aucune facture ne doit être en attente de paiement.',
  'Les données, factures et paiements existants sont conservés.',
  'Le pèlerin sera retiré de son groupement actuel, le cas échéant.',
  'Les nouvelles factures seront rattachées à la nouvelle société.',
  'Le transfert ne peut pas être annulé.',
];

const groupRules = [
  'Aucun pèlerin concerné ne doit avoir de facture en attente de paiement.',
  'Le groupement sera conservé et rattaché à la nouvelle société.',
  'Les données, factures et paiements existants sont conservés.',
  'Les nouvelles factures seront rattachées à la nouvelle société.',
  'Le transfert ne peut pas être annulé.',
];

const RetryAlert = ({ title, onRetry }) => (
  <Alert variant="destructive">
    <AlertCircle />
    <AlertTitle>{title}</AlertTitle>
    <AlertDescription>
      <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
        Réessayer
      </Button>
    </AlertDescription>
  </Alert>
);

RetryAlert.propTypes = { title: PropTypes.string.isRequired, onRetry: PropTypes.func.isRequired };

const TransferAgencyModal = ({
  isOpened = false,
  pilgrimData = null,
  agencies = null,
  isAgenciesLoading = false,
  agencyError = false,
  isSubmitting = false,
  isReconciling = false,
  isRetryBlocked = false,
  transferError = null,
  onRetryAgencies = () => {},
  onRetryVerification = () => {},
  onSubmit = () => {},
  onClose = () => {},
}) => {
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const sourceAgencyId = findAgencyByName(pilgrimData?.agency_name, agencies)?.id;
  const isGroupTransfer = pilgrimData?.self_registered === true && !!pilgrimData?.group_id;
  const targetAgencies = Array.isArray(agencies)
    ? agencies.filter((agency) => agency?.id && agency.id !== sourceAgencyId)
    : [];
  const isBusy = isSubmitting || isReconciling;
  const sourceUnavailable = !sourceAgencyId;
  const canSubmit =
    targetAgencies.some((agency) => agency.id === selectedAgencyId) &&
    !isBusy &&
    !agencyError &&
    !sourceUnavailable &&
    !isRetryBlocked;

  return (
    <Dialog open={isOpened} onOpenChange={(open) => !open && !isBusy && onClose()}>
      <DialogContent
        closeDisabled={isBusy}
        overlayClassName="bg-[#13231c66]"
        className="flex max-h-[calc(100dvh-1.5rem)] w-[calc(100%-1.5rem)] max-w-[660px] flex-col gap-0 overflow-hidden rounded-[10px] border-[#dce4df] bg-white p-0 font-[Inter,sans-serif] shadow-[0_18px_60px_#15281d26] sm:max-h-[calc(100dvh-2.5rem)]"
        onEscapeKeyDown={(event) => isBusy && event.preventDefault()}
        onPointerDownOutside={(event) => isBusy && event.preventDefault()}
      >
        <DialogHeader className="shrink-0 space-y-1 border-b border-[#e2e7e4] px-4 py-4 pr-12 text-left sm:px-6 sm:py-5">
          <DialogTitle className="text-base font-semibold text-[#202d28]">Transférer le pèlerin</DialogTitle>
          <DialogDescription className="text-[13px] text-[#6b7772]">
            Choisissez la nouvelle société agréée.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
          <>
            <dl className="mb-4 grid grid-cols-1 gap-3 rounded-[7px] border border-[#e5ebe7] bg-[#f7f9f8] px-4 py-3 sm:grid-cols-2 sm:gap-x-6">
              <div className="min-w-0">
                <dt className="text-[11px] text-[#748078]">Pèlerin</dt>
                <dd className="break-words text-[13px] font-semibold text-[#24352c]">
                  {getUserName(pilgrimData?.pilgrim?.first_name, pilgrimData?.pilgrim?.last_name)}
                </dd>
                <dt className="mt-1 text-[11px] text-[#748078]">NPI</dt>
                <dd className="break-words text-xs text-[#52645a]">{pilgrimData?.pilgrim?.npi || 'Non renseigné'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-[11px] text-[#748078]">Société actuelle</dt>
                <dd className="break-words text-[13px] font-medium text-[#24352c]">
                  {pilgrimData?.agency_name || 'Non renseignée'}
                </dd>
              </div>
            </dl>

            {isGroupTransfer && (
              <section className="mb-4 rounded-[7px] border border-[#dfe8e2] bg-[#f6f9f7] px-4 py-3 text-[13px] text-[#52645a]">
                <h3 className="font-semibold text-[#334a3b]">Transfert du groupement</h3>
                <p className="mt-1.5 leading-5">
                  Ce pèlerin s&apos;est inscrit avec un groupement. Le transfert concernera également les autres
                  pèlerins du même groupement.
                </p>
                <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3">
                  <dt className="text-[#748078]">Groupement</dt>
                  <dd className="break-words font-medium text-[#334a3b]">
                    {pilgrimData?.group_name || 'Non renseigné'}
                  </dd>
                </dl>
              </section>
            )}

            <section className="mb-4 border-b border-[#e2e7e4] pb-4">
              <h3 className="mb-1.5 text-[13px] font-semibold text-[#334a3b]">À savoir</h3>
              <ul className="list-disc space-y-0.5 pl-5 text-xs leading-[19px] text-[#52645a]">
                {(isGroupTransfer ? groupRules : normalRules).map((rule, index) => (
                  <li key={rule} className={index === 0 ? 'font-semibold text-[#334a3b]' : undefined}>
                    {rule}
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h3 className="mb-2.5 text-[13px] font-semibold text-[#334a3b]">Nouvelle société agréée</h3>
              {sourceUnavailable && (
                <Alert variant="destructive" className="mb-3 py-3">
                  <AlertCircle />
                  <AlertDescription>Impossible d&apos;identifier la société actuelle du pèlerin.</AlertDescription>
                </Alert>
              )}
              {isAgenciesLoading ? (
                <div className="flex min-h-24 items-center justify-center gap-2 text-sm text-[#64746a]">
                  <Spinner /> Chargement des sociétés…
                </div>
              ) : agencyError ? (
                <RetryAlert title="Impossible de charger les sociétés agréées" onRetry={onRetryAgencies} />
              ) : targetAgencies.length === 0 ? (
                <p className="rounded-[7px] border border-dashed px-4 py-5 text-center text-sm text-[#6b7772]">
                  Aucune autre société agréée disponible.
                </p>
              ) : (
                <RadioGroup
                  value={selectedAgencyId}
                  onValueChange={setSelectedAgencyId}
                  aria-label="Choisissez une seule société agréée"
                  disabled={isBusy}
                >
                  {targetAgencies.map((agency) => {
                    const selected = agency.id === selectedAgencyId;
                    const email = agency.admin?.email || agency.email;
                    return (
                      <label
                        key={agency.id}
                        htmlFor={`transfer-agency-${agency.id}`}
                        className={cn(
                          'grid cursor-pointer grid-cols-[minmax(0,1fr)_18px] items-start gap-4 rounded-[7px] border border-[#dfe6e2] bg-white px-4 py-3 focus-within:ring-2 focus-within:ring-hajj-primary',
                          selected && 'border-hajj-primary bg-[#f1f8f4]',
                          isBusy && 'cursor-not-allowed opacity-60'
                        )}
                      >
                        <span className="min-w-0 text-xs leading-[18px] text-[#6b7772]">
                          <span className="block break-words text-[13px] font-semibold text-[#202d28]">
                            {agency.name}
                          </span>
                          {email && <span className="block break-all">{email}</span>}
                          <span className="grid gap-x-5 sm:grid-cols-[minmax(0,1fr)_auto]">
                            {agency.address && <span className="break-words">{agency.address}</span>}
                            {agency.phone && <span className="break-all text-[#52645a]">{agency.phone}</span>}
                          </span>
                        </span>
                        <RadioGroupItem
                          id={`transfer-agency-${agency.id}`}
                          value={agency.id}
                          disabled={isBusy}
                          className="mt-0.5 data-[state=checked]:border-hajj-primary data-[state=checked]:text-hajj-primary"
                        />
                      </label>
                    );
                  })}
                </RadioGroup>
              )}
            </section>
          </>
        </div>

        <DialogFooter className="shrink-0 border-t bg-white px-4 py-3 sm:px-6">
          {transferError && (
            <Alert variant="destructive" className="mb-2 w-full py-2.5 sm:mr-auto sm:max-w-[360px]">
              <AlertCircle />
              <AlertDescription>
                {transferError}
                {isRetryBlocked && (
                  <Button variant="outline" size="sm" className="ml-3" onClick={onRetryVerification}>
                    Réessayer
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}
          <Button variant="outline" onClick={onClose} disabled={isBusy}>
            Annuler
          </Button>
          <Button variant="hajj" onClick={() => canSubmit && onSubmit(selectedAgencyId)} disabled={!canSubmit}>
            {isSubmitting ? (
              <>
                <Spinner /> Transfert en cours…
              </>
            ) : isReconciling ? (
              <>
                <Spinner /> Vérification en cours…
              </>
            ) : (
              'Transférer'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

TransferAgencyModal.propTypes = {
  isOpened: PropTypes.bool,
  pilgrimData: PropTypes.shape({}),
  agencies: PropTypes.arrayOf(PropTypes.shape({})),
  isAgenciesLoading: PropTypes.bool,
  agencyError: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isReconciling: PropTypes.bool,
  isRetryBlocked: PropTypes.bool,
  transferError: PropTypes.string,
  onRetryAgencies: PropTypes.func,
  onRetryVerification: PropTypes.func,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
};

export default TransferAgencyModal;
